import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ResponsiveImage } from "@/components/site/ResponsiveImage";
import { ManagedAssetImage } from "@/components/site/ManagedAssetImage";
import buddhaKaImage from "@/assets/buddha-ka-official.png.asset.json";
import { preloadImageLink } from "@/lib/images";
import { BOOKS, RELEASES, formatReleaseDate } from "@/lib/content";
import { SITE_URL } from "@/lib/site";
import { PORTRAIT_URL, PRESS_KIT_ID, breadcrumbs, ldScript, orgRef, pageNode, personRef, seoMeta } from "@/lib/schema";
import { track } from "@/lib/analytics";
import {
  ANGLES,
  BIO_400,
  BOILERPLATE,
  DIAMOND_SUTRA_STATEMENT,
  FACT_SHEET,
  PRESS_ASSETS,
  PRESS_BIO_100,
  PRESS_BIO_30,
  PRESS_EMAIL,
  PRESS_OFFICIAL_LINKS,
  PRESS_RESOURCES,
  USAGE_TERMS,
} from "@/lib/press";
import { PRESS_SLOTS, type PressBrandAssetMap } from "@/lib/press-brand-assets";
import { getPressBrandAssets } from "@/lib/press-brand-assets.functions";

import { getNews, getPressReleases } from "@/lib/news";
import { trackMarketingPressClick } from "@/lib/marketing/analytics";


export const Route = createFileRoute("/press/")({
  loader: async (): Promise<{ brandAssets: PressBrandAssetMap }> => ({
    brandAssets: await getPressBrandAssets(),
  }),

  head: () => {
    const url = `${SITE_URL}/press`;
    const title = "Buddha KA Press Kit | Biography, Photos, Music & Contact";
    const description =
      "Official Buddha KA press kit — biographies, official portrait, fact sheet, story angles, streaming links, and press contact for KAfrequency.";
    return {
      ...seoMeta({
        title,
        description,
        path: "/press",
        image: PORTRAIT_URL,
        imageAlt: "Buddha KA — official press portrait",
        ogType: "profile",
      }),
      // This array replaces seoMeta()'s links, so the production canonical is
      // re-declared here alongside the LCP preload and the syndication feeds.
      links: [
        { rel: "canonical", href: url },
        preloadImageLink(buddhaKaImage.url, "(max-width: 768px) 92vw, 460px"),
        {
          rel: "alternate",
          type: "application/rss+xml",
          title: "Buddha KA press releases (RSS)",
          href: `${SITE_URL}/press/releases.xml`,
        },
        {
          rel: "alternate",
          type: "application/rss+xml",
          title: "Buddha KA newsroom (RSS)",
          href: `${SITE_URL}/feed.xml`,
        },
        {
          rel: "alternate",
          type: "application/atom+xml",
          title: "Buddha KA newsroom (Atom)",
          href: `${SITE_URL}/atom.xml`,
        },
        {
          rel: "alternate",
          type: "application/json",
          title: "Buddha KA electronic press kit (JSON)",
          href: `${SITE_URL}/press/kit.json`,
        },
      ].filter(Boolean) as Array<Record<string, string>>,
      scripts: [
        // Official EPK. Person/Organization are defined once sitewide and
        // referenced by @id here.
        ldScript(
          pageNode({
            type: "ProfilePage",
            id: PRESS_KIT_ID,
            url,
            name: "Buddha KA Press Kit",
            description,
            image: PORTRAIT_URL,
            imageCaption: "Buddha KA — official press portrait",
            mainEntity: personRef(),
            about: [personRef(), orgRef()],
            extra: {
              publisher: orgRef(),
              significantLink: [
                `${SITE_URL}/about`,
                `${SITE_URL}/music`,
                `${SITE_URL}/news`,
                `${SITE_URL}/press/releases`,
                `${SITE_URL}/contact`,
              ],
              // Machine-readable press resources, declared on the page itself.
              subjectOf: PRESS_RESOURCES.map((r) => ({
                "@type": "DataFeed",
                name: r.label,
                url: `${SITE_URL}${r.path}`,
                encodingFormat: r.type,
              })),
              associatedMedia: PRESS_ASSETS.map((a) => ({
                "@type": "ImageObject",
                name: a.label,
                contentUrl: a.url,
                creditText: a.credit,
                copyrightHolder: orgRef(),
              })),
            },
          }),
        ),
        ldScript(breadcrumbs([["Press kit", "/press"]])),
      ],
    };
  },

  component: PressPage,
});

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          track("press_kit_copy", { asset: label });
          setTimeout(() => setCopied(false), 1800);
        } catch {
          setCopied(false);
        }
      }}
      aria-label={`Copy ${label}`}
      className="inline-flex min-h-9 items-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-3 py-1 text-xs text-gold-luminous hover:bg-gold hover:text-obsidian focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
    >
      <span aria-hidden>{copied ? "Copied" : `Copy ${label}`}</span>
      <span className="sr-only" role="status" aria-live="polite">
        {copied ? `${label} copied to clipboard` : ""}
      </span>
    </button>
  );
}

function PressPage() {
  const { brandAssets } = Route.useLoaderData();
  const news = getNews().slice(0, 3);
  const pressReleases = getPressReleases().slice(0, 3);
  const [failedSlots, setFailedSlots] = useState<Record<string, boolean>>({});
  const markFailed = (slot: string) => setFailedSlots((s) => ({ ...s, [slot]: true }));
  const heroAssetRaw = brandAssets["buddha-ka-press-primary"];
  const heroAsset = heroAssetRaw && !failedSlots[heroAssetRaw.slot] ? heroAssetRaw : undefined;
  const heroSrc = heroAsset?.url ?? buddhaKaImage.url;
  const heroAlt = heroAsset?.alt || "Buddha KA — official Buddha KA artwork";
  const photoSlots = PRESS_SLOTS.filter(
    (s) => s.slot === "buddha-ka-press-primary" && brandAssets[s.slot] && !failedSlots[s.slot],
  );
  const logoSlots = PRESS_SLOTS.filter(
    (s) => s.kind === "logo" && brandAssets[s.slot] && !failedSlots[s.slot],
  );
  useEffect(() => {
    track("press_kit_view", { surface: "press" });
  }, []);
  return (

    <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-24">
      <header className="grid gap-10 md:grid-cols-[1fr_1.1fr] md:items-center">
        <div className="rounded-2xl border border-gold/40 bg-charcoal p-3 shadow-halo">
          {heroAsset ? (
            <ManagedAssetImage
              src={heroAsset.url}
              alt={heroAlt}
              loading="eager"
              onFail={() => markFailed(heroAsset.slot)}
              className="aspect-[4/5] w-full rounded-xl object-cover"
            />
          ) : (
            <ResponsiveImage
              src={buddhaKaImage.url}
              alt="Buddha KA — official Buddha KA artwork"
              priority
              sizes="(max-width: 768px) 92vw, 460px"
              className="aspect-[4/5] w-full rounded-xl object-cover"
            />
          )}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 px-1 pb-1 text-xs">
            <span className="text-muted-foreground">
              {heroAsset ? "Approved press photograph · Buddha KA" : "Official artwork · Buddha KA"}
            </span>
            <a
              href={heroSrc}
              download
              target="_blank"
              rel="noreferrer"
              className="rounded-full border border-gold/50 px-3 py-1 text-gold hover:bg-gold hover:text-obsidian"
            >
              Open original ↗
            </a>
          </div>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Press Kit</p>
          <h1 className="mt-3 font-serif text-4xl leading-tight sm:text-5xl md:text-6xl">
            Buddha KA — official press materials.
          </h1>
          <p className="mt-5 text-lg text-ivory/85">
            Independent Chicago-born recording artist, author, and founder of KAfrequency. Out now:
            The Diamond Sutra — released August 19, 2026.
          </p>
          <p className="mt-4 text-sm text-muted-foreground">
            Press enquiries:{" "}
            <a
              href={`mailto:${PRESS_EMAIL}`}
              onClick={() => {
                track("press_email_click", { surface: "press" });
                trackMarketingPressClick({ action: "email", surface: "press" });
              }}
              className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold"
            >
              {PRESS_EMAIL}
            </a>
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/press/the-diamond-sutra" className="rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian shadow-gold">
              The Diamond Sutra press kit
            </Link>
            <Link
              to="/music/$slug"
              params={{ slug: "the-diamond-sutra" }}
              className="rounded-full border border-gold px-5 py-2.5 text-sm text-gold hover:bg-gold hover:text-obsidian"
            >
              The Diamond Sutra release page
            </Link>

            <Link to="/contact" className="rounded-full border border-gold px-5 py-2.5 text-sm text-gold hover:bg-gold hover:text-obsidian">
              Press contact
            </Link>
            <Link to="/news" className="rounded-full border border-gold px-5 py-2.5 text-sm text-gold hover:bg-gold hover:text-obsidian">
              Official newsroom
            </Link>
            <Link to="/artist/buddha-ka" className="rounded-full border hairline px-5 py-2.5 text-sm hover:border-gold hover:text-gold">
              Official Buddha KA artist page
            </Link>
            <Link to="/about" className="rounded-full border hairline px-5 py-2.5 text-sm hover:border-gold hover:text-gold">
              Buddha KA biography
            </Link>
            <Link to="/music" className="rounded-full border hairline px-5 py-2.5 text-sm hover:border-gold hover:text-gold">
              KAfrequency music catalog
            </Link>
            <Link to="/books" className="rounded-full border hairline px-5 py-2.5 text-sm hover:border-gold hover:text-gold">
              Books by Buddha KA
            </Link>

          </div>
        </div>
      </header>

      <section className="mt-16">
        <div className="flex items-end justify-between gap-4">
          <h2 className="font-serif text-3xl">Short bio · 30 words</h2>
          <CopyButton text={PRESS_BIO_30} label="30-word bio" />
        </div>
        <p className="mt-4 rounded-xl border hairline bg-charcoal p-5 text-ivory/90">{PRESS_BIO_30}</p>
      </section>

      <section className="mt-12">
        <div className="flex items-end justify-between gap-4">
          <h2 className="font-serif text-3xl">Standard bio · ~100 words</h2>
          <CopyButton text={PRESS_BIO_100} label="100-word bio" />
        </div>
        <p className="mt-4 rounded-xl border hairline bg-charcoal p-5 text-ivory/90">{PRESS_BIO_100}</p>
      </section>

      <section className="mt-12">
        <div className="flex items-end justify-between gap-4">
          <h2 className="font-serif text-3xl">Full bio</h2>
          <CopyButton text={BIO_400.join("\n\n")} label="full bio" />
        </div>
        <div className="mt-4 space-y-4 rounded-xl border hairline bg-charcoal p-5 text-ivory/90">
          {BIO_400.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </section>

      <section className="mt-12">
        <div className="flex items-end justify-between gap-4">
          <h2 className="font-serif text-3xl">The Diamond Sutra — official statement</h2>
          <CopyButton text={DIAMOND_SUTRA_STATEMENT.join("\n\n")} label="album statement" />
        </div>
        <div className="mt-4 space-y-4 rounded-xl border border-gold/30 bg-charcoal p-5 text-ivory/90">
          {DIAMOND_SUTRA_STATEMENT.map((p, i) => <p key={i}>{p}</p>)}
          <p className="text-sm">
            <Link to="/press/the-diamond-sutra" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">
              Full Diamond Sutra press kit →
            </Link>
          </p>
        </div>
      </section>


      <section className="mt-12">
        <h2 className="font-serif text-3xl">Fact sheet</h2>
        <dl className="mt-4 grid gap-3 rounded-xl border hairline bg-charcoal p-5 sm:grid-cols-2">
          {FACT_SHEET.map(([k, v]) => (
            <div key={k} className="border-b hairline pb-3 last:border-b-0">
              <dt className="text-[10px] uppercase tracking-[0.25em] text-gold">{k}</dt>
              <dd className="mt-1 text-sm text-ivory/90">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mt-12">
        <h2 className="font-serif text-3xl">Selected works</h2>
        <div className="mt-4 grid gap-6 sm:grid-cols-2">
          <div>
            <h3 className="text-[10px] uppercase tracking-[0.25em] text-gold">Music</h3>
            <ul className="mt-3 space-y-2 text-sm text-ivory/90">
              {RELEASES.map((r) => (
                <li key={r.slug}>
                  <Link to="/music/$slug" params={{ slug: r.slug }} className="hover:text-gold-luminous">
                    {r.title}
                  </Link>{" "}
                  <span className="text-xs text-muted-foreground">
                    ({r.type}
                    {r.year ? `, ${r.year}` : ""}
                    {r.status === "upcoming"
                      ? `, arrives ${formatReleaseDate(r) ?? "soon"}`
                      : r.status === "in-progress"
                        ? ", in progress"
                        : ""})
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-[10px] uppercase tracking-[0.25em] text-gold">Books &amp; written systems</h3>
            <ul className="mt-3 space-y-2 text-sm text-ivory/90">
              {BOOKS.map((b) => (
                <li key={b.slug}>
                  <Link to="/books/$slug" params={{ slug: b.slug }} className="hover:text-gold-luminous">
                    {b.title}
                  </Link>{" "}
                  <span className="text-xs text-muted-foreground">({b.category})</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="mt-12">
        <h2 className="font-serif text-3xl">Interview & story angles</h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {ANGLES.map((a) => (
            <li key={a} className="rounded-xl border hairline bg-charcoal p-4 text-sm text-ivory/90">{a}</li>
          ))}
        </ul>
      </section>

      <section className="mt-12">
        <h2 className="font-serif text-3xl">Official destinations</h2>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          Verified first-party pages and official artist profiles. Nothing here is an unverified or third-party
          listing.
        </p>
        <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {PRESS_OFFICIAL_LINKS.map(({ label: k, url }) => (

            <li key={k}>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  track("press_outbound_click", { platform: k, url });
                  trackMarketingPressClick({ action: "outbound", platform: k, surface: "press" });
                }}
                className="flex min-h-11 items-center justify-center rounded-full border border-gold px-3 py-2 text-xs text-gold hover:bg-gold hover:text-obsidian"
              >
                {k} ↗
              </a>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-12">
        <h2 className="font-serif text-3xl">Latest official news</h2>
        {news[0] && (
          <p className="mt-3 text-sm text-muted-foreground">
            Most recent official statement:{" "}
            <Link to="/news/$slug" params={{ slug: news[0].slug }} className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">
              {news[0].title}
            </Link>{" "}
            ({news[0].publishedAt}) — see the full{" "}
            <Link to="/news" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Buddha KA official newsroom</Link>.
          </p>
        )}
        <ul className="mt-4 grid gap-4 sm:grid-cols-3">

          {news.map((n) => (
            <li key={n.slug}>
              <Link
                to="/news/$slug"
                params={{ slug: n.slug }}
                className="group block h-full rounded-xl border hairline bg-charcoal p-4 hover:border-gold"
              >
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{n.category}</p>
                <h3 className="mt-2 font-serif text-lg text-ivory group-hover:text-gold-luminous">{n.title}</h3>
                <p className="mt-2 line-clamp-3 text-xs text-muted-foreground">{n.dek}</p>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-12">
        <h2 className="font-serif text-3xl">Press releases</h2>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          Formal, first-party announcements. Every release is archived and syndicated — nothing here is third-party
          media coverage.
        </p>
        <ul className="mt-4 space-y-3">
          {pressReleases.map((n) => (
            <li key={n.slug} className="rounded-xl border hairline bg-charcoal p-4">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                {n.category} · <time dateTime={n.publishedAt}>{n.publishedAt}</time>
              </p>
              <Link
                to="/news/$slug"
                params={{ slug: n.slug }}
                className="mt-2 block font-serif text-lg text-ivory hover:text-gold-luminous"
              >
                {n.title}
              </Link>
              <p className="mt-1 text-xs text-muted-foreground">{n.dek}</p>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          <Link to="/press/releases" className="rounded-full border border-gold px-4 py-2 text-gold hover:bg-gold hover:text-obsidian">
            Press release archive
          </Link>
          <a
            href="/press/releases.xml"
            onClick={() => track("press_resource_click", { resource: "press-releases-rss" })}
            className="rounded-full border hairline px-4 py-2 hover:border-gold hover:text-gold"
          >
            Press release RSS
          </a>
        </div>
      </section>

      {photoSlots.length > 0 && (
        <section className="mt-12">
          <h2 className="font-serif text-3xl">Press photography</h2>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            Approved press photographs. Download at full resolution and credit as listed. Do not crop, recolour, or
            overlay.
          </p>
          <ul className="mt-4 grid gap-4 sm:grid-cols-2">
            {photoSlots.map((def) => {
              const a = brandAssets[def.slot]!;
              return (
                <li key={def.slot} className="overflow-hidden rounded-xl border hairline bg-charcoal">
                  <ManagedAssetImage
                    src={a.url}
                    alt={a.alt || def.caption}
                    onFail={() => markFailed(def.slot)}
                    className="aspect-[4/5] w-full object-cover"
                  />
                  <div className="p-4">
                    <p className="font-serif text-sm text-ivory">{def.caption}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{a.credit}</p>
                    <a
                      href={a.url}
                      download
                      onClick={() => track("press_kit_asset_download", { asset: def.slot })}
                      className="mt-3 inline-flex min-h-9 items-center rounded-full border border-gold/50 bg-gold/10 px-3 py-1 text-xs text-gold-luminous hover:bg-gold hover:text-obsidian"
                    >
                      Download
                    </a>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {logoSlots.length > 0 && (
        <section className="mt-12">
          <h2 className="font-serif text-3xl">KAfrequency logos</h2>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            Official KAfrequency marks. Reproduce as supplied, on dark backgrounds wherever possible.
          </p>
          <ul className="mt-4 grid gap-4 sm:grid-cols-2">
            {logoSlots.map((def) => {
              const a = brandAssets[def.slot]!;
              return (
                <li key={def.slot} className="overflow-hidden rounded-xl border hairline bg-obsidian">
                  <ManagedAssetImage
                    src={a.url}
                    alt={a.alt || def.caption}
                    onFail={() => markFailed(def.slot)}
                    className="mx-auto h-40 w-full object-contain p-6"
                  />
                  <div className="border-t hairline p-4">
                    <p className="font-serif text-sm text-ivory">{def.caption}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{a.credit}</p>
                    <a
                      href={a.url}
                      download
                      onClick={() => track("press_kit_asset_download", { asset: def.slot })}
                      className="mt-3 inline-flex min-h-9 items-center rounded-full border border-gold/50 bg-gold/10 px-3 py-1 text-xs text-gold-luminous hover:bg-gold hover:text-obsidian"
                    >
                      Download
                    </a>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="mt-12">
        <h2 className="font-serif text-3xl">Cover artwork &amp; official imagery</h2>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          Approved, first-party imagery. Download at full resolution and credit as listed.
        </p>
        <ul className="mt-4 grid gap-4 sm:grid-cols-3">

          {PRESS_ASSETS.map((a) => (
            <li key={a.url} className="overflow-hidden rounded-xl border hairline bg-charcoal">
              <img src={a.url} alt={a.alt} loading="lazy" decoding="async" className="aspect-square w-full object-cover" />
              <div className="p-4">
                <p className="font-serif text-sm text-ivory">{a.label}</p>
                <p className="mt-1 text-xs text-muted-foreground">{a.credit}</p>
                <a
                  href={a.url}
                  download
                  onClick={() => track("press_kit_asset_download", { asset: a.label })}
                  className="mt-3 inline-flex min-h-9 items-center rounded-full border border-gold/50 bg-gold/10 px-3 py-1 text-xs text-gold-luminous hover:bg-gold hover:text-obsidian"
                >
                  Download
                </a>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-12">
        <h2 className="font-serif text-3xl">Machine-readable press resources</h2>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          Syndication endpoints for newsrooms, aggregators, and agents.
        </p>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {PRESS_RESOURCES.map((r) => (
            <li key={r.path}>
              <a
                href={r.path}
                onClick={() => track("press_resource_click", { resource: r.path })}
                className="flex h-full flex-col rounded-xl border hairline bg-charcoal p-4 hover:border-gold"
              >
                <span className="font-serif text-sm text-ivory">{r.label}</span>
                <span className="mt-1 text-xs text-muted-foreground">{r.description}</span>
                <code className="mt-2 text-[11px] text-gold">{r.path}</code>
              </a>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-12 rounded-2xl border hairline bg-obsidian/60 p-6 text-sm text-muted-foreground">
        <h2 className="font-serif text-xl text-ivory">Boilerplate</h2>
        <p className="mt-2 text-ivory/85">{BOILERPLATE}</p>
        <div className="mt-3">
          <CopyButton text={BOILERPLATE} label="boilerplate" />
        </div>
      </section>

      <section className="mt-12 rounded-2xl border hairline bg-obsidian/60 p-6 text-sm text-muted-foreground">
        <h2 className="font-serif text-xl text-ivory">Usage note</h2>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {USAGE_TERMS.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
        <p className="mt-3">
          For any use outside these terms, request written permission via{" "}
          <Link to="/contact" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">/contact</Link>.
        </p>
      </section>

      <section className="mt-12" aria-labelledby="press-contact">
        <h2 id="press-contact" className="font-serif text-3xl">Press contact</h2>
        <p className="mt-3 text-sm text-muted-foreground">
          Interviews, review copies, artwork requests, and verification:{" "}
          <a
            href={`mailto:${PRESS_EMAIL}`}
            onClick={() => track("press_email_click", { surface: "press-footer" })}
            className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold"
          >
            {PRESS_EMAIL}
          </a>
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <a
            href={`mailto:${PRESS_EMAIL}`}
            onClick={() => track("press_email_click", { surface: "press-cta" })}
            className="rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold"
          >
            Email press
          </a>
          <Link to="/contact" className="rounded-full border border-gold px-6 py-3 text-sm text-gold hover:bg-gold hover:text-obsidian">
            Press contact form
          </Link>
          <Link to="/news" className="rounded-full border border-gold px-6 py-3 text-sm text-gold hover:bg-gold hover:text-obsidian">
            Official newsroom
          </Link>
        </div>
      </section>

    </div>
  );
}
